import React, { useEffect, useState, useRef, createElement } from 'react';
import { Note, User } from '../../types';
import { Avatar } from '../ui/Avatar';
import { Badge } from '../ui/Badge';
import { Clock, MoreHorizontal, ChevronDown, ChevronUp, Reply, Send, Copy, Check, Trash2, AtSign, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { RichTextEditor } from './RichTextEditor';
interface NoteCardProps {
  note: Note;
  onReply: (parentId: string, content: string, taggedUsers?: User[]) => void;
  onDelete: (noteId: string) => void;
  availableUsers?: User[];
}
const CATEGORY_VARIANTS: Record<string, 'default' | 'success' | 'warning' | 'danger' | 'info'> = {
  Underwriting: 'warning',
  Processing: 'info',
  Closing: 'success',
  Call: 'default',
  Email: 'default',
  General: 'default'
};
const AVATAR_COLORS: Record<string, string> = {
  u1: 'bg-blue-100 text-blue-700',
  u2: 'bg-slate-100 text-slate-600',
  u3: 'bg-purple-100 text-purple-700',
  u4: 'bg-green-100 text-green-700',
  u5: 'bg-orange-100 text-orange-700',
  me: 'bg-teal-100 text-teal-700'
};
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric'
  }).format(date);
}
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
function getFileIcon(type: string) {
  if (type.startsWith('image/')) return Image;
  if (type.includes('pdf') || type.includes('document') || type.includes('text')) return FileText;
  return File;
}
export function NoteCard({
  note,
  onReply,
  onDelete,
  availableUsers = []
}: NoteCardProps) {
  const [isReplying, setIsReplying] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [repliesExpanded, setRepliesExpanded] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [replyTaggedUsers, setReplyTaggedUsers] = useState<User[]>([]);
  const [replyTagPickerOpen, setReplyTagPickerOpen] = useState(false);
  const [replyTagSearch, setReplyTagSearch] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);
  const replyTagPickerRef = useRef<HTMLDivElement>(null);
  const replyTagSearchRef = useRef<HTMLInputElement>(null);
  const avatarColor = AVATAR_COLORS[note.author.id] || 'bg-slate-100 text-slate-600';
  const categoryVariant = CATEGORY_VARIANTS[note.category] || 'default';
  const replyCount = note.replies?.length || 0;
  // Close menu on click outside
  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [menuOpen]);
  // Close reply tag picker on click outside
  useEffect(() => {
    if (!replyTagPickerOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (replyTagPickerRef.current && !replyTagPickerRef.current.contains(e.target as Node)) {
        setReplyTagPickerOpen(false);
        setReplyTagSearch('');
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setReplyTagPickerOpen(false);
        setReplyTagSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [replyTagPickerOpen]);
  useEffect(() => {
    if (replyTagPickerOpen && replyTagSearchRef.current) {
      replyTagSearchRef.current.focus();
    }
  }, [replyTagPickerOpen]);
  const toggleReplyTagUser = (user: User) => {
    setReplyTaggedUsers(prev => prev.some(u => u.id === user.id) ? prev.filter(u => u.id !== user.id) : [...prev, user]);
  };
  const filteredReplyUsers = availableUsers.filter(u => u.name.toLowerCase().includes(replyTagSearch.toLowerCase()) || u.role.toLowerCase().includes(replyTagSearch.toLowerCase()));
  const handleCopyText = async () => {
    const tmp = document.createElement('div');
    tmp.innerHTML = note.content;
    const text = tmp.textContent || tmp.innerText || '';
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for non-secure contexts (iframes, http)
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        textarea.style.top = '-9999px';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopySuccess(true);
      setTimeout(() => {
        setCopySuccess(false);
        setMenuOpen(false);
      }, 1000);
    } catch {
      // Last resort fallback
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      textarea.style.top = '-9999px';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      try {
        document.execCommand('copy');
        setCopySuccess(true);
        setTimeout(() => {
          setCopySuccess(false);
          setMenuOpen(false);
        }, 1000);
      } catch {
        setMenuOpen(false);
      }
      document.body.removeChild(textarea);
    }
  };
  const handleSubmitReply = () => {
    if (!replyContent.trim()) return;
    onReply(note.id, replyContent, replyTaggedUsers.length > 0 ? replyTaggedUsers : undefined);
    setReplyContent('');
    setReplyTaggedUsers([]);
    setIsReplying(false);
  };
  return <div className="relative pl-10 pb-6 last:pb-0 group/timeline" data-id="element-1">
      {/* Timeline connector line */}
      <div className="absolute left-4 top-9 bottom-0 w-px bg-slate-200 group-last/timeline:hidden" data-id="element-2" />

      {/* Author avatar — sits on the timeline */}
      <div className="absolute left-0 top-1" data-id="element-3">
        <Avatar initials={note.author.initials} size="sm" className={`ring-2 ring-white ${avatarColor}`} data-id="element-4" />
      </div>

      {/* Note card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200" data-id="element-5">
        {/* Card header */}
        <div className="flex items-start justify-between px-4 py-3" data-id="element-6">
          <div className="flex-1 min-w-0 pr-3" data-id="element-7">
            <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-8">
              <span className="text-sm font-semibold text-slate-800" data-id="element-9">
                {note.author.name}
              </span>
              <span className="text-[11px] text-slate-400" data-id="element-10">·</span>
              <span className="text-[11px] text-slate-400 flex items-center gap-1" data-id="element-11">
                <Clock className="h-3 w-3" data-id="element-12" />
                {formatRelativeTime(note.createdAt)}
              </span>
            </div>
            <h4 className="text-[13px] font-semibold text-slate-700 leading-snug" data-id="element-13">
              {note.subject}
            </h4>
            {/* Tagged Users */}
            {note.taggedUsers && note.taggedUsers.length > 0 && <div className="flex items-center gap-1.5 mt-1.5 flex-wrap" data-id="element-14">
                <AtSign className="h-3 w-3 text-blue-400 shrink-0" data-id="element-15" />
                {note.taggedUsers.map(user => <span key={user.id} className="text-[11px] font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full border border-blue-100" data-id="element-16">
                    {user.name}
                  </span>)}
              </div>}
          </div>

          <div className="flex items-center gap-2 shrink-0 mt-0.5" data-id="element-17">
            <Badge variant={categoryVariant} data-id="element-18">{note.category}</Badge>
          </div>
        </div>

        {/* Card body — always visible */}
        <div className="px-4 pb-4 border-t border-slate-100" data-id="element-19">
          <div className="prose prose-sm max-w-none text-slate-600 pt-3 leading-relaxed [&_p]:mb-2 [&_ul]:mt-1" dangerouslySetInnerHTML={{
          __html: note.content
        }} data-id="element-20" />

          {/* Replies Section */}
          {replyCount > 0 && <div className="mt-4" data-id="element-21">
              <button onClick={() => setRepliesExpanded(!repliesExpanded)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-blue-600 transition-colors mb-2 py-1" data-id="element-22">
                {repliesExpanded ? <ChevronUp className="h-3.5 w-3.5" data-id="element-23" /> : <ChevronDown className="h-3.5 w-3.5" data-id="element-24" />}
                {repliesExpanded ? 'Hide' : 'Show'} {replyCount}{' '}
                {replyCount === 1 ? 'reply' : 'replies'}
              </button>
              {repliesExpanded && <div className="space-y-2.5 pl-4 border-l-2 border-blue-100" data-id="element-25">
                  {note.replies!.map(reply => {
              const replyAvatarColor = AVATAR_COLORS[reply.author.id] || 'bg-slate-100 text-slate-600';
              return <div key={reply.id} className="bg-slate-50 rounded-lg p-3" data-id="element-26">
                        <div className="flex items-center gap-2 mb-1.5" data-id="element-27">
                          <Avatar initials={reply.author.initials} size="sm" className={`h-5 w-5 text-[9px] ${replyAvatarColor}`} data-id="element-28" />
                          <span className="text-xs font-semibold text-slate-700" data-id="element-29">
                            {reply.author.name}
                          </span>
                          <span className="text-[11px] text-slate-400" data-id="element-30">
                            · {formatRelativeTime(reply.createdAt)}
                          </span>
                        </div>
                        <div className="prose prose-sm max-w-none text-slate-600 pl-7" dangerouslySetInnerHTML={{
                  __html: reply.content
                }} data-id="element-31" />
                        {/* Tagged users on reply */}
                        {reply.taggedUsers && reply.taggedUsers.length > 0 && <div className="flex items-center gap-1 mt-2 pl-7 flex-wrap" data-id="element-32">
                            <AtSign className="h-3 w-3 text-blue-400 shrink-0" data-id="element-33" />
                            {reply.taggedUsers.map(user => <span key={user.id} className="text-[10px] font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full border border-blue-100" data-id="element-34">
                                {user.name}
                              </span>)}
                          </div>}
                      </div>;
            })}
                </div>}
            </div>}

          {/* Reply Composer */}
          {isReplying && <div className="mt-4 animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-35">
              {/* Tagged user pills */}
              {replyTaggedUsers.length > 0 && <div className="flex flex-wrap gap-1.5 mb-2" data-id="element-36">
                  {replyTaggedUsers.map(user => <span key={user.id} className="inline-flex items-center gap-1 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full" data-id="element-37">
                      @{user.name}
                      <button type="button" onClick={() => setReplyTaggedUsers(prev => prev.filter(u => u.id !== user.id))} className="text-blue-400 hover:text-blue-600 transition-colors" data-id="element-38">
                        <X className="h-3 w-3" data-id="element-39" />
                      </button>
                    </span>)}
                </div>}
              <div className="mb-2" data-id="element-40">
                <RichTextEditor value={replyContent} onChange={setReplyContent} placeholder="Write a reply..." minHeight="100px" data-id="element-41" />
              </div>
              <div className="flex items-center justify-between gap-2" data-id="element-42">
                {/* Tag button */}
                <div className="relative" ref={replyTagPickerRef} data-id="element-43">
                  <button type="button" onClick={() => setReplyTagPickerOpen(!replyTagPickerOpen)} className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1.5 rounded-md transition-colors ${replyTaggedUsers.length > 0 ? 'text-blue-700 bg-blue-50 border border-blue-200' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100 border border-transparent'}`} data-id="element-44">
                    <AtSign className="h-3.5 w-3.5" data-id="element-45" />
                    Tag
                    {replyTaggedUsers.length > 0 ? ` (${replyTaggedUsers.length})` : ''}
                  </button>
                  {replyTagPickerOpen && <div className="absolute left-0 bottom-full mb-1 w-56 bg-white rounded-lg border border-slate-200 shadow-lg z-30 animate-in fade-in slide-in-from-bottom-2 duration-150" data-id="element-46">
                      <div className="p-2 border-b border-slate-100" data-id="element-47">
                        <input ref={replyTagSearchRef} type="text" placeholder="Search users..." value={replyTagSearch} onChange={e => setReplyTagSearch(e.target.value)} className="w-full text-xs px-2.5 py-1.5 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-slate-400" data-id="element-48" />
                      </div>
                      <div className="max-h-40 overflow-y-auto py-1" data-id="element-49">
                        {filteredReplyUsers.length === 0 ? <p className="text-xs text-slate-400 text-center py-3" data-id="element-50">
                            No users found
                          </p> : filteredReplyUsers.map(user => {
                    const isSelected = replyTaggedUsers.some(u => u.id === user.id);
                    return <button key={user.id} type="button" onClick={() => toggleReplyTagUser(user)} className={`w-full flex items-center gap-2 px-3 py-1.5 text-left transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-slate-50'}`} data-id="element-51">
                                <div className={`h-5 w-5 rounded-full flex items-center justify-center text-[9px] font-bold shrink-0 ${isSelected ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`} data-id="element-52">
                                  {user.initials}
                                </div>
                                <div className="flex-1 min-w-0" data-id="element-53">
                                  <p className="text-xs font-medium text-slate-800 truncate" data-id="element-54">
                                    {user.name}
                                  </p>
                                </div>
                                {isSelected && <div className="h-3.5 w-3.5 rounded-full bg-blue-600 flex items-center justify-center shrink-0" data-id="element-55">
                                    <svg className="h-2 w-2 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-56">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-57" />
                                    </svg>
                                  </div>}
                              </button>;
                  })}
                      </div>
                    </div>}
                </div>
                <div className="flex items-center gap-2" data-id="element-58">
                  <Button variant="ghost" size="sm" onClick={() => {
                setIsReplying(false);
                setReplyTaggedUsers([]);
              }} data-id="element-59">
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleSubmitReply} disabled={!replyContent.trim()} data-id="element-60">
                    <Send className="h-3 w-3 mr-1.5" data-id="element-61" />
                    Send Reply
                  </Button>
                </div>
              </div>
            </div>}

          {/* Actions Footer */}
          {!isReplying && <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100" data-id="element-62">
              <button onClick={() => setIsReplying(true)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-blue-600 transition-colors py-1" data-id="element-63">
                <Reply className="h-3.5 w-3.5" data-id="element-64" />
                Reply
                {replyCount > 0 && <span className="text-[10px] text-slate-300 font-normal" data-id="element-65">
                    ({replyCount})
                  </span>}
              </button>
              <div className="relative" ref={menuRef} data-id="element-66">
                <button onClick={() => setMenuOpen(!menuOpen)} className={`p-0.5 rounded transition-colors ${menuOpen ? 'text-slate-600 bg-slate-100' : 'text-slate-300 hover:text-slate-500'}`} data-id="element-67">
                  <MoreHorizontal className="h-4 w-4" data-id="element-68" />
                </button>
                {menuOpen && <div className="absolute right-0 bottom-full mb-1 w-44 bg-white rounded-lg border border-slate-200 shadow-lg py-1 z-20 animate-in fade-in slide-in-from-bottom-2 duration-150" data-id="element-69">
                    <button onClick={handleCopyText} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors" data-id="element-70">
                      {copySuccess ? <>
                          <Check className="h-3.5 w-3.5 text-green-600" data-id="element-71" />
                          <span className="text-green-600" data-id="element-72">Copied!</span>
                        </> : <>
                          <Copy className="h-3.5 w-3.5" data-id="element-73" />
                          Copy Text
                        </>}
                    </button>
                    <div className="h-px bg-slate-100 my-1" data-id="element-74" />
                    <button onClick={() => {
                onDelete(note.id);
                setMenuOpen(false);
              }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors" data-id="element-75">
                      <Trash2 className="h-3.5 w-3.5" data-id="element-76" />
                      Delete Note
                    </button>
                  </div>}
              </div>
            </div>}
        </div>
      </div>
    </div>;
}